import React from 'react';
import PageHero from '@/components/site/PageHero';
import StartButton from '@/components/site/StartButton';
import GoldText from '@/components/site/GoldText';
import WorkflowPreview from '@/components/home/WorkflowPreview';
import { workflows } from '@/content/workflows';
import { useRovingTabs } from '@/hooks/useRovingTabs';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn } from '@/lib/utils';

const UseCases = () => {
  usePageMeta(
    'Use Cases',
    'Practical starting points for an AI assistant pilot: lead response, client intake, internal knowledge, and admin coordination.',
  );
  const { selected, tabProps } = useRovingTabs(workflows.length);
  const w = workflows[selected];

  return (
    <>
      <PageHero
        eyebrow="Use cases"
        showCta={false}
        title={
          <>
            Find the work <GoldText>worth improving.</GoldText>
          </>
        }
      >
        <p>
          Explore practical starting points for an AI assistant. Your pilot begins by selecting one workflow your
          business can test meaningfully.
        </p>
      </PageHero>

      <section aria-label="Use case explorer" className="border-t border-rule">
        <div className="section-container">
          <div role="tablist" aria-label="Use cases" className="mb-8 flex flex-wrap gap-2">
            {workflows.map((wf, i) => (
              <button
                key={wf.id}
                {...tabProps(i)}
                id={`usecase-tab-${wf.id}`}
                aria-controls="usecase-panel"
                className={cn(
                  'border px-4 py-2.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors',
                  i === selected
                    ? 'border-goldcore text-goldcore'
                    : 'border-rule2 text-softwhite/85 hover:border-mist/50',
                )}
              >
                {wf.name}
              </button>
            ))}
          </div>

          <div
            id="usecase-panel"
            role="tabpanel"
            aria-labelledby={`usecase-tab-${w.id}`}
            className="grid gap-6 lg:grid-cols-[1.1fr_1fr]"
          >
            <dl className="surface divide-y divide-rule">
              {[
                ['The current task', w.currentTask],
                ['Proposed assistant role', w.assistantRole],
                ['Human review point', w.reviewPoint],
                ['Measure to evaluate', w.measure],
              ].map(([term, desc]) => (
                <div key={term} className="p-6">
                  <dt className="meta-label mb-2">{term}</dt>
                  <dd className="font-serif text-2xl leading-snug">{desc}</dd>
                </div>
              ))}
            </dl>
            <div className="flex flex-col gap-6">
              <WorkflowPreview workflow={w} />
              <p className="text-sm text-mist">Illustrative sample using fictional business information.</p>
            </div>
          </div>

          <div className="reveal mt-16 flex flex-col items-start gap-5 border-y border-rule py-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-serif text-3xl leading-tight sm:text-4xl">
                Which workflow looks <GoldText>closest to yours?</GoldText>
              </p>
              <p className="mt-1 text-mist">
                We’ll bring <span className="text-softwhite">{w.name.toLowerCase()}</span> into your discovery call—you
                can change it on the next step.
              </p>
            </div>
            <StartButton interest={w.id} className="shrink-0" />
          </div>
        </div>
      </section>
    </>
  );
};

export default UseCases;
